/* ==========================================================================
   Segal — "The name is written" waiting state
   --------------------------------------------------------------------------
   During the ~30s generate, the customer's own name is drawn in gold script,
   stroke by stroke, OVER THE PRODUCT IMAGE - the place they are already
   looking, and the place the finished necklace will appear.

   2026-09-01, second cut. The first version rendered this inside the Generate
   block next to the button. Yaniv: "i wanted the gif to be on the picture, not
   in the bar." It now mounts as an overlay on `.product-gallery__main`.

   That move also removes a duplication the first cut created: the switcher has
   its own placeholder (`.segal-name-drawing`) that writes the same name over
   the same gallery. While this overlay is up that placeholder is hidden, and it
   is restored on teardown, so only one animation is ever on screen.

   WHY THIS IS ITS OWN FILE. It touches nothing. It watches for the
   `.snui--busy` class that name-necklace-ui.js already sets, renders an
   overlay, and tears itself down when busy clears. Deleting the one <script>
   line in snippets/name-necklace-switcher.liquid removes the feature and
   restores the previous behaviour exactly.

   BOTH FAMILIES. The animation is the typed name, so it is identical on plain
   gold and diamond pages; only the phase wording differs, keyed off
   window.SegalNameNecklace.family. Nothing here can affect which product gets
   created - it never touches fetch, the form, or the variant.

   POLLING, NOT OBSERVING. The theme replaces the whole product subtree on a
   product swap, which detaches any MutationObserver bound to it. A 250ms
   interval survives that; the existing UI already ticks at 300ms.
   ========================================================================== */
(() => {
  'use strict';

  const GOLD     = '#b78b43';   // brand gold, same value the UI uses
  const INK_SOFT = 'rgba(53,78,87,.6)';
  const DRAW_MS  = 26000;       // measured generates run 28-40s; the stroke
                                // finishes near 80% and the fill holds.
  const VB_W = 600, VB_H = 240;
  const TICK = 250;

  const reduce = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cfg = window.SegalNameNecklace || {};
  const IS_DIAMOND = cfg.family === 'diamond';

  const PHASES = IS_DIAMOND
    ? ['Drawing the letterforms', 'Setting the stones', 'Almost there']
    : ['Drawing the letterforms', 'Joining the strokes', 'Almost there'];

  /* ---------------------------------------------------------------- font --
     Great Vibes is the closest Google face to the script Segal actually casts.
     If it fails to load the stroke draw still reads correctly in the fallback
     cursive - the effect is the animation, not the typeface.                 */
  const FONT_STACK = "'Great Vibes','Snell Roundhand','Brush Script MT',cursive,serif";
  (function loadFont() {
    if (document.getElementById('segal-nn-script-font')) return;
    const l = document.createElement('link');
    l.id = 'segal-nn-script-font';
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
    document.head.appendChild(l);
  })();

  /* ----------------------------------------------------------------- css -- */
  (function injectCss() {
    if (document.getElementById('segal-nn-draw-css')) return;
    const s = document.createElement('style');
    s.id = 'segal-nn-draw-css';
    s.textContent = `
.snd{position:absolute;inset:0;z-index:6;pointer-events:none;
  background:radial-gradient(120% 90% at 50% 38%,#ffffff 0%,#fbf8f1 62%,#f5efe3 100%);
  display:flex;flex-direction:column;align-items:center;justify-content:center}
.snd__svg{display:block;width:82%;max-width:560px;height:auto}
.snd__phase{margin-top:10px;font-size:11.5px;letter-spacing:.14em;text-transform:uppercase;
  color:${INK_SOFT};text-align:center;padding:0 16px;min-height:15px}
.snd__ink{fill:none;stroke:${GOLD};stroke-width:1.5;stroke-linecap:round;stroke-linejoin:round}
.snd__fill{fill:${GOLD};opacity:0}
.snd--static .snd__fill{opacity:1}`;
    document.head.appendChild(s);
  })();

  /* --------------------------------------------------------------- build -- */
  const SVGNS = 'http://www.w3.org/2000/svg';

  function buildStage(name) {
    const wrap = document.createElement('div');
    wrap.className = 'snd';
    wrap.setAttribute('aria-hidden', 'true');   // the Generate block speaks for a11y

    const svg = document.createElementNS(SVGNS, 'svg');
    svg.setAttribute('class', 'snd__svg');
    svg.setAttribute('viewBox', `0 0 ${VB_W} ${VB_H}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    const g = document.createElementNS(SVGNS, 'g');

    const mk = (cls) => {
      const t = document.createElementNS(SVGNS, 'text');
      t.setAttribute('class', cls);
      t.setAttribute('x', String(VB_W / 2));
      t.setAttribute('y', String(VB_H / 2));
      t.setAttribute('text-anchor', 'middle');
      t.setAttribute('font-family', FONT_STACK);
      t.setAttribute('font-size', '104');
      t.textContent = name;
      return t;
    };

    const ink = mk('snd__ink');
    const fill = mk('snd__fill');
    g.appendChild(ink);
    g.appendChild(fill);
    svg.appendChild(g);

    const phase = document.createElement('div');
    phase.className = 'snd__phase';
    phase.textContent = PHASES[0];

    wrap.appendChild(svg);
    wrap.appendChild(phase);
    return { wrap, svg, g, ink, fill, phase };
  }

  /* Scale the word to fit. Long names (up to the 9-character cap) would
     otherwise run off both edges.                                            */
  function fit(parts) {
    let bb;
    try { bb = parts.g.getBBox(); } catch (_) { return 1500; }
    if (!bb || !bb.width) return 1500;

    const padX = 30, padY = 20;
    const s = Math.min(1, (VB_W - padX * 2) / bb.width, (VB_H - padY * 2) / bb.height);
    const cx = bb.x + bb.width / 2;
    const cy = bb.y + bb.height / 2;
    parts.g.setAttribute(
      'transform',
      `translate(${VB_W / 2} ${VB_H / 2}) scale(${s.toFixed(4)}) translate(${-cx} ${-cy})`
    );

    // A script glyph's outline runs roughly 3-4x its bounding width, and there is
    // no getTotalLength() for <text>. The failure directions are NOT equal:
    //   under-estimate -> the dash pattern REPEATS and the word appears in
    //                     disconnected pieces at once. Looks broken.
    //   over-estimate  -> the stroke completes early and holds. Looks fine.
    // So over-estimate deliberately.
    return Math.max(700, Math.round(bb.width * 4.0));
  }

  /* WHY THE WEB ANIMATIONS API AND NOT CSS. The first cut passed the dash length
     and duration in as custom properties - `animation: sndDraw var(--snd-dur)`
     with `@keyframes{from{stroke-dashoffset:var(--snd-dash)}}`. Measured over a
     real 36s generate: stroke-dashoffset never moved. It sat at its start value
     for 27 seconds then snapped to 0, and the fill's calc() delay also failed to
     apply, so the finished word appeared immediately. element.animate() takes
     real numbers and cannot be lost to a shorthand parse.

     MEASURE ONLY AFTER THE WEBFONT IS IN, or getBBox() returns the fallback
     cursive's metrics. Hard 1500ms cap so a blocked Google Fonts cannot leave
     an empty panel. */
  function start(parts) {
    const timers = [];
    let started = false;

    const go = () => {
      if (started || !parts.wrap.isConnected) return;
      started = true;

      const dash = fit(parts);
      parts.ink.style.strokeDasharray = String(dash);
      parts.ink.style.strokeDashoffset = String(dash);

      if (reduce || typeof parts.ink.animate !== 'function') {
        parts.wrap.classList.add('snd--static');
        parts.ink.style.strokeDashoffset = '0';
        return;
      }

      parts.ink.animate(
        [{ strokeDashoffset: dash }, { strokeDashoffset: 0 }],
        { duration: DRAW_MS, easing: 'linear', fill: 'forwards' }
      );
      parts.fill.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 1400, delay: Math.round(DRAW_MS * 0.9), easing: 'ease-out', fill: 'forwards' }
      );

      timers.push(setTimeout(() => { parts.phase.textContent = PHASES[1]; }, DRAW_MS * 0.42));
      timers.push(setTimeout(() => { parts.phase.textContent = PHASES[2]; }, DRAW_MS * 0.78));
    };

    if (document.fonts && typeof document.fonts.load === 'function') {
      try { document.fonts.load('104px "Great Vibes"').then(go, go); }
      catch (_) { go(); }
      timers.push(setTimeout(go, 1500));
    } else {
      go();
    }

    return () => { timers.forEach(clearTimeout); };
  }

  /* ------------------------------------------------- switcher placeholder --
     The switcher writes the same name over the same gallery. Hide it while we
     are up so the customer never sees the name animated twice, and put it back
     exactly as found on teardown.                                            */
  function hideSwitcherDrawing() {
    const hidden = [];
    document.querySelectorAll('.segal-name-drawing').forEach((el) => {
      hidden.push([el, el.style.display]);
      el.style.display = 'none';
    });
    return hidden;
  }
  function restoreSwitcherDrawing(hidden) {
    (hidden || []).forEach(([el, prev]) => { el.style.display = prev || ''; });
  }

  /* ---------------------------------------------------------------- loop -- */
  let mounted = null;   // { parts, name, cancel, hidden }

  function teardown() {
    if (!mounted) return;
    if (mounted.cancel) mounted.cancel();
    restoreSwitcherDrawing(mounted.hidden);
    if (mounted.parts.wrap.parentNode) mounted.parts.wrap.parentNode.removeChild(mounted.parts.wrap);
    mounted = null;
  }

  function typedName(box) {
    const el = box.querySelector('.snui__in');
    return el ? String(el.value || '').trim() : '';
  }

  // The gallery column. `.product-gallery__main` already carries position-relative
  // in this theme, so an inset:0 overlay lands on the image exactly.
  function stageHost() {
    return document.querySelector('.product-gallery__main')
        || document.querySelector('.product-gallery__main_slider-wrapper')
        || null;
  }

  setInterval(() => {
    try {
      const box = document.querySelector('.snui');
      if (!box) { teardown(); return; }

      const busy = box.classList.contains('snui--busy');
      if (!busy) { teardown(); return; }

      const name = typedName(box);
      if (!name) { teardown(); return; }

      const host = stageHost();
      if (!host) { teardown(); return; }

      if (mounted && mounted.name === name && mounted.parts.wrap.isConnected) return;

      teardown();

      const parts = buildStage(name);
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(parts.wrap);

      const hidden = hideSwitcherDrawing();
      const cancel = start(parts);
      mounted = { parts, name, cancel, hidden };
    } catch (_) {
      // A waiting animation must never break the page it waits on.
    }
  }, TICK);
})();
